import React, { Suspense, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { matchPath, Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { userSelectors } from 'reducers';
import { useSubscriptionFormSignatureStatus, useUserRoleMatchCategory } from 'utils/hooks';
import { roleCategories } from 'utils/constants';

import { Loader } from 'components';

const useIsPathMatched = routes => {
    const location = useLocation();

    return routes.some(route =>
        matchPath(
            { path: route.path, exact: route.exact, strict: route.strict || false },
            location.pathname
        )
    );
};

const useReturnToParam = () => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    return params.get('returnTo');
};

const AppRoutes = props => {
    const { routes, fallbackPath, loggedIn } = props;

    const location = useLocation();

    const isPathMatched = useIsPathMatched(routes);
    const returnTo = useReturnToParam();
    const userIsInvestor = useUserRoleMatchCategory(roleCategories.INVESTORS);
    const userIsDistributor = useUserRoleMatchCategory([
        roleCategories.ADMIN_DISTRIBUTORS,
        roleCategories.DISTRIBUTORS,
    ]);

    const currentSubscription = useSelector(userSelectors.getCurrentSubscription);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const hasFinishedSubscription = useSelector(userSelectors.getFinishedSubscriptionStatus);
    const hasInProgressSubscription = useSelector(userSelectors.getInProgressSubscriptionStatus);
    const subscriberSignedBS = useSubscriptionFormSignatureStatus(tunnelSubscription);

    const renderRoute = route => {
        const routeComponent = (
            <Suspense fallback={<Loader />}>
                <route.component />
            </Suspense>
        );

        return (
            <Route
                key={route.key}
                path={route.path}
                exact={route.exact}
                element={route.protected && !loggedIn ? <Navigate to="/sign-in" /> : routeComponent}
            />
        );
    };

    const distributorPreviewClientSpace = useMemo(
        () => currentSubscription?.distributorCompany?.documentsValidated,
        [currentSubscription]
    );

    const redirectRoute = useMemo(() => {
        if (userIsInvestor && !location.pathname.includes('conditions-generales')) {
            if (
                hasInProgressSubscription &&
                !hasFinishedSubscription &&
                subscriberSignedBS &&
                !location.pathname.includes('dashboard') &&
                !location.pathname.includes('investor-documents') &&
                !location.pathname.includes('sign-up-success') &&
                !location.pathname.includes('sign-up-error')
            ) {
                return '/dashboard';
            } else if (
                hasInProgressSubscription &&
                !subscriberSignedBS &&
                !hasFinishedSubscription &&
                !location.pathname.includes('sign-up') &&
                !location.pathname.includes('abandonner')
            ) {
                return '/sign-up';
            }
        }

        if (userIsDistributor && !location.pathname.includes('conditions-generales')) {
            if (
                !hasFinishedSubscription &&
                distributorPreviewClientSpace &&
                !location.pathname.includes('homepage') &&
                !location.pathname.includes('products') &&
                !location.pathname.includes('distributor-documents') &&
                !location.pathname.includes('distributor-users')
            ) {
                return '/homepage';
            }
            if (
                !hasFinishedSubscription &&
                !distributorPreviewClientSpace &&
                !location.pathname.includes('onboarding')
            ) {
                return '/onboarding';
            }
        }

        if ((!!fallbackPath && !isPathMatched) || !!returnTo) {
            let target = fallbackPath;
            if (returnTo) {
                try {
                    target = new URL(returnTo).pathname;
                } catch {
                    /* ignore */
                }
            }
            return target;
        }
    }, [
        distributorPreviewClientSpace,
        fallbackPath,
        hasFinishedSubscription,
        hasInProgressSubscription,
        isPathMatched,
        location.pathname,
        returnTo,
        subscriberSignedBS,
        userIsDistributor,
        userIsInvestor,
    ]);

    return (
        <Routes>
            {routes.map(renderRoute)}
            {!!redirectRoute && (
                <Route path="*" element={<Navigate to={redirectRoute} replace />} />
            )}
        </Routes>
    );
};

AppRoutes.propTypes = {
    routes: PropTypes.array.isRequired,
    loggedIn: PropTypes.bool.isRequired,
    fallbackPath: PropTypes.string,
};

export default AppRoutes;
